import cx from 'classnames';
import { Container, enums as e, Pill, Tooltip, utils as u } from 'edrolo-ui';
import { useAnalytics } from 'edrolo/analytics';
import { UserContext } from 'edrolo/context';
import {
  useStartOfDay,
  useTasksStudent,
} from 'edrolo/domains/coursePortal/hooks';
import urlResolver from 'edrolo/utils/URLResolver';
import { WaffleFlag } from 'edrolo/utils/Waffle';
import pluralize from 'pluralize';
import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AssignmentLifecycleStage, TaskStatus } from '../../types/Task';
import { getCategorizedSchoolClasses } from '../../utils/utils';
import { EdroloLogo } from './EdroloLogo';
import s from './NavBar.scss';
import { Menu, ProfileMenu } from './components';
import { MenuItem } from './type';
import { getProfileMenuItems, profileItems } from './utils';
import { useProfile } from 'edrolo/domains/profile';
import { Profile } from 'edrolo/types/Profile';

export const NavBar = () => {
  const { profile } = useContext(UserContext);
  const { track } = useAnalytics({});
  const { useKeyStore } = useProfile();
  const [teacherHub] = useKeyStore('teacher_hub', false);
  const [studentHub] = useKeyStore('student_hub', false);

  const categorizedSchoolClasses: Record<string, MenuItem[]> =
    getCategorizedSchoolClasses(profile?.school_classes);

  const profileMenuItems = getProfileMenuItems(profile);
  const tasksUrl = urlResolver.reverse('tasks-page', {});

  const teacherHubEnabled =
    waffle.flag_is_active('teacher-hub') &&
    teacherHub === true &&
    profile.is_teacher;
  const studentHubEnabled =
    waffle.flag_is_active('student-hub') &&
    studentHub === true &&
    !profile.is_teacher;

  const homeUrl = teacherHubEnabled
    ? '/home'
    : studentHubEnabled
    ? '/student'
    : `/`;

  return (
    <div className={s.navBar}>
      <Container width={e.Width.FULL}>
        <nav className={s.inner}>
          <div className={s.left}>
            <a href={homeUrl}>
              <EdroloLogo />
            </a>
            {profile && (
              <Menu
                buttonTitle={profile.is_teacher ? 'Classes' : 'Subjects'}
                menuItems={categorizedSchoolClasses}
              />
            )}
            <WaffleFlag name="task_setting">
              <Link
                className={cx(u.flex, u.gap2, u.alignCenter)}
                to={tasksUrl}
                onClick={() => track('Tasks clicked')}
              >
                <span>Tasks</span>

                <TasksAlert profile={profile} />
              </Link>
            </WaffleFlag>
          </div>

          {profile && (
            <div className={s.right}>
              <ProfileMenu
                buttonTitle={profile.display_name}
                menuItems={profileMenuItems.sort((a, b) => a.id - b.id)}
              />
            </div>
          )}
        </nav>
      </Container>
    </div>
  );
};

const TasksAlert = ({ profile }: { profile?: Profile }) => {
  if (!profile || profile.is_teacher) {
    return null;
  }

  return <TasksAlertContent />;
};

const TasksAlertContent = () => {
  const currentDay = useStartOfDay();

  // FIXME: we should add a specific endpoint for counting incomplete tasks
  const { data, isLoading } = useTasksStudent({
    status: [TaskStatus.OPEN],
    assignmentStage: [
      AssignmentLifecycleStage.NOT_STARTED,
      AssignmentLifecycleStage.IN_PROGRESS,
    ],
    // Only show tasks that have passed their start_datetime (filter out scheduled tasks)
    startDatetimeLte: currentDay,
    // Reasonable limit for counting incomplete tasks
    pageSize: 100,
  });

  if (isLoading) {
    return null;
  }

  const incompleteCount = data?.length || 0;

  if (!incompleteCount) {
    return null;
  }

  const displayCount = incompleteCount > 99 ? '99+' : incompleteCount;

  return (
    <Tooltip
      tip={`You have ${incompleteCount} ${pluralize(
        'task',
        incompleteCount
      )} to complete`}
    >
      <Pill
        variant={e.PillVariant.CIRCLE}
        color={e.ColorHue.TEAL}
        size={e.Size.SMALL}
        className={s.tasksAlert}
        ariaLabel={`${incompleteCount} incomplete tasks`}
      >
        <span className={u.surface7}>{displayCount}</span>
      </Pill>
    </Tooltip>
  );
};
